import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useAutoSave Hook
 * Automatically saves data at regular intervals
 *
 * @param {Object} data - Data to save
 * @param {Function} saveFunction - Async function to call for saving
 * @param {Number} interval - Save interval in milliseconds (default: 30000 = 30 seconds)
 * @param {Boolean} enabled - Whether auto-save is enabled (default: true)
 */
export const useAutoSave = (data, saveFunction, interval = 30000, enabled = true) => {
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const savedDataRef = useRef(null);
  const timeoutRef = useRef(null);
  const latestRef = useRef({ data, saveFunction, enabled });

  // Keep latestRef current on every render
  useEffect(() => {
    latestRef.current = { data, saveFunction, enabled };
  });

  /**
   * Check if data has changed since last save
   */
  const hasChanged = useCallback(() => {
    if (!savedDataRef.current) return true;
    return JSON.stringify(data) !== JSON.stringify(savedDataRef.current);
  }, [data]);

  /**
   * Perform save operation
   */
  const performSave = useCallback(async () => {
    if (!enabled || !data || !saveFunction) return;

    // Don't save if data hasn't changed
    if (!hasChanged()) {
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      await saveFunction(data);

      savedDataRef.current = JSON.parse(JSON.stringify(data)); // Deep copy
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save error:', error);
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  }, [enabled, data, saveFunction, hasChanged]);

  /**
   * Manual save trigger
   */
  const saveNow = useCallback(async () => {
    // Clear any pending auto-save
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    await performSave();

    // Restart auto-save timer
    if (enabled && interval > 0) {
      timeoutRef.current = setTimeout(performSave, interval);
    }
  }, [performSave, enabled, interval]);

  /**
   * Auto-save effect
   */
  useEffect(() => {
    if (!enabled || interval <= 0 || !data || !saveFunction) return;

    // Set up auto-save interval
    timeoutRef.current = setTimeout(performSave, interval);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, saveFunction, interval, enabled, performSave]);

  /**
   * Save on unmount (page navigation)
   */
  useEffect(() => {
    return () => {
      const { data: d, saveFunction: fn, enabled: en } = latestRef.current;
      if (en && fn && JSON.stringify(d) !== JSON.stringify(savedDataRef.current)) {
        fn(d).catch(err => console.error('Save on unmount failed:', err));
      }
    };
  }, []);

  return {
    lastSaved,
    isSaving,
    saveError,
    saveNow,
    hasUnsavedChanges: hasChanged()
  };
};
